export const formatScoreTick = (score: number): string => score === 0 ? "Unknown" : score.toString();

/** Formats number using "k" and "M" postfixes and rounds to at most 2 decimals trimming trailing zeros.
 * @example ```"1.25k", "12.5k", "4M"```
 */
export const formatCountTick = (value: number): string => {
  const magnitude = Math.floor(Math.log10(Math.abs(value)));
  if (magnitude < 3) {
    return value.toLocaleString();
  }

  let unitPower = Math.min(6, magnitude - (magnitude % 3)); // 3 = "k", 6 = "M"
  let displayNumber = Math.round((value / Math.pow(10, unitPower)) * 100) / 100;

  // Rounding the mantissa can carry it up to the next unit (e.g. 999996 -> 1000k, not 1M).
  if (Math.abs(displayNumber) >= 1000 && unitPower < 6) {
    unitPower += 3;
    displayNumber = Math.round((value / Math.pow(10, unitPower)) * 100) / 100;
  }

  const unitSuffix = unitPower >= 6 ? "M" : "k";
  return `${displayNumber.toLocaleString()}${unitSuffix}`;
};

/** Add's 15% of maximum value to Y axis to leave room for "Avg" label. */
export const padYAxis = (dataMax: number): number => Math.max(1, dataMax + Math.ceil(dataMax * 0.15));

/** Rounds a raw step size UP to the nearest "nice" step. Never returns a step below 1,
 * since ticks here are always whole provider counts.
 */
const getNiceStep = (rawStep: number): number => {
  let magnitude = 1;
  while (magnitude * 10 <= rawStep) {
    magnitude *= 10;
  }

  const normalized = rawStep / magnitude;
  if (normalized <= 1)
    return magnitude;
  if (normalized <= 2)
    return magnitude * 2;
  if (magnitude > 1 && normalized <= 2.5)
    // Only whole number steps allowed (allow 25 but not 2.5)
    return magnitude * 2.5;
  if (normalized <= 5)
    return magnitude * 5;
  return magnitude * 10;
};

/** Calculates `numTicks` evenly spaced tick marks from 0 to a nice round number >= dataMax,
 * choosing the step size that fits the data best.
 */
export const getYAxisTicks = (dataMax: number, numTicks: number): number[] => {
  const ticksWithoutZero = numTicks - 1; // First tick is for "0"
  const max = Math.max(1, dataMax);
  const niceStep = getNiceStep(max / ticksWithoutZero);
  return Array.from({ length: numTicks }, (_, index) => index * niceStep);
};

const MIN_VISIBLE_BAR_HEIGHT_PX = 3;
/** Gives non-zero bars a minimum pixel height so they are always visible. */
export const getMinBarHeight = (value: number): number => value > 0 ? MIN_VISIBLE_BAR_HEIGHT_PX : 0;
